import dayjs from "dayjs";
import { useMemo, useEffect, useState } from "react";
import { useForm, FormProvider, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { Container, LinearProgress } from "@mui/material";
import {
  financialFormSchema,
  type FinancialFormValues,
} from "../adapaters/financialFormSchema";
import {
  viewToForm,
  recordToSectionArray,
} from "../adapaters/financialFormAdapter";
import {
  steps,
  categoryByKey,
  titleByKey,
  StepId,
} from "../adapaters/financialStatementMeta";
import { computeTotals } from "../domain/entities/calculators";
import { CategoryInputsPanel } from "./CategoryInputsPanel";
import { FlexibleStep } from "./Flexible";
import AboutYou from "./AboutYou";
import { FixedExtras } from "./FixedExtras";
import {
  employmentTitleIdMap,
  employmentTypeIdMap,
} from "../domain/entities/employment";
import { buildIncome, buildFixed, buildFlexible, buildDebts } from "../domain/entities/mapping";
import { useNavigate, useParams } from "react-router-dom";
import ScrollToTop from "../../../components/nav/ScrollToTop";
import { Frequency } from "../domain/entities/multiplier";
import { useFinancialStatementQuery } from "../queries/useSlimFinancialQuery";
import { useSaveFinancialMutation } from "../mutations/useSaveFinancialMutation";
import { createStepNavigator } from "../hooks/useCreateStepNavigator";
import { stepOrder } from "../shared/utils";
import { useNav } from "../../../context/NavProvider";
import { useAnalytics } from "../../../context/AnalyticsProvider";
import { ANALYTICS_TAGS } from "../../../constants/analyticsTags";

export function FinancialSteps() {
  const navigate = useNavigate();
  const { setShowFooter } = useNav();
  const { setTag } = useAnalytics();

  const { step } = useParams<{ step?: StepId }>();
  const [completed, setCompleted] = useState<boolean[]>([]);

  const financialQuery = useFinancialStatementQuery();
  const saveFinancialMutation = useSaveFinancialMutation();

  const defaults = useMemo(
    () =>
      viewToForm({
        income: financialQuery.data?.income ?? buildIncome(null),
        fixed: financialQuery.data?.fixed ?? buildFixed(null),
        flexible: financialQuery.data?.flexible ?? buildFlexible(null),
        debts: financialQuery.data?.debts ?? buildDebts(null),
        monthlySavingsAmount: financialQuery.data?.monthlySavingsAmount ?? 0,
        fixedOther: financialQuery.data?.fixedOther ?? [],
        aboutYou: {
          customersEmploymentStatus:
            financialQuery.data?.customersEmploymentStatus ?? "UNK",
          partnersEmploymentStatus:
            financialQuery.data?.partnersEmploymentStatus ?? "UNK",
          homeStatus: financialQuery.data?.homeStatus ?? "",
          employmentTypeId:
            financialQuery.data?.employmentType ?? employmentTypeIdMap.Unknown,
          employmentTitleId:
            financialQuery.data?.employmentTitle ??
            employmentTitleIdMap.Unknown,
          adults: financialQuery.data?.adults ?? 0,
          childrenUnder16: financialQuery.data?.childrenUnder16 ?? 0,
          children16to18: financialQuery.data?.children16to18 ?? 0,
          vehicles: financialQuery.data?.numberOfVehicles ?? 0,
        },
      }),
    [financialQuery.data],
  );

  const methods = useForm<FinancialFormValues>({
    resolver: zodResolver(financialFormSchema) as any,
    defaultValues: defaults,
    mode: "onChange",
  });

  const { control, handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const watchedIncome = useWatch({ control, name: "income" });
  const watchedFixed = useWatch({ control, name: "fixed" });
  const watchedFlexible = useWatch({ control, name: "flexible" });
  const watchedDebts = useWatch({ control, name: "debts" });
  const watchedSavings = useWatch({ control, name: "monthlySavingsAmount" });
  const watchedFixedOther = useWatch({ control, name: "fixedOther" });

  const totals = useMemo(() => {
    const incomeArr = recordToSectionArray(
      watchedIncome ?? {},
      "Income",
      categoryByKey,
      titleByKey,
    );

    const fixedArrBase = recordToSectionArray(
      watchedFixed ?? {},
      "Fixed Expenditure",
      categoryByKey,
      titleByKey,
    );

    const fixedOtherArr = (watchedFixedOther ?? [])
      .filter((o) => (o?.amount ?? 0) > 0)
      .map((o, idx) => ({
        parent: "Fixed Expenditure",
        category: "Other",
        title: o.label || `Other item ${idx + 1}`,
        value: o.amount ?? 0,
        frequency: (o.frequency as Frequency) ?? ("M" as Frequency),
        key: `fixedOther_${idx}`,
      }));

    const fixedArr = [...fixedArrBase, ...fixedOtherArr];

    const flexArr = recordToSectionArray(
      watchedFlexible ?? {},
      "Flexible Expenditure",
      categoryByKey,
      titleByKey,
    );

    const debtArr = recordToSectionArray(
      watchedDebts ?? {},
      "Additional Debt",
      categoryByKey,
      titleByKey,
    );

    return computeTotals(
      incomeArr,
      fixedArr,
      flexArr,
      debtArr,
      fixedOtherArr,
      watchedSavings ?? 0,
    );
  }, [
    watchedIncome,
    watchedFixed,
    watchedFlexible,
    watchedDebts,
    watchedSavings,
    watchedFixedOther,
  ]);

  // Track whether flexible triggers have been acknowledged (set by FlexibleStep)
  const triggersAckCompleteFlexible = useWatch({
    control,
    name: "triggersAckCompleteFlexible",
  });

  const {
    activeStepIndex,
    activeStepId,
    goToStep,
    nextStep: nextStepBase,
    prevStep: prevStepBase,
  } = createStepNavigator<StepId>({
    stepOrder,
    currentStep: step,
    basePath: "/sfs",
    navigate,
  });

  const nextStep = () => {
    const nextIndex = activeStepIndex + 1;
    if (nextIndex < steps.length) {
      setTag(ANALYTICS_TAGS.SFS_STEP_CHANGE, {
        step: steps[nextIndex].id,
        stepNumber: nextIndex + 1,
      });
    }
    nextStepBase();
  };

  const prevStep = () => {
    const prevIndex = activeStepIndex - 1;
    if (prevIndex >= 0) {
      setTag(ANALYTICS_TAGS.SFS_STEP_CHANGE, {
        step: steps[prevIndex].id,
        stepNumber: prevIndex + 1,
      });
    }
    prevStepBase();
  };

  const onSubmit = async (values: FinancialFormValues) => {
    const incomeArr = recordToSectionArray(
      values.income ?? {},
      "Income",
      categoryByKey,
      titleByKey,
    );
    const fixedArr = recordToSectionArray(
      values.fixed ?? {},
      "Fixed Expenditure",
      categoryByKey,
      titleByKey,
    );
    const flexArr = recordToSectionArray(
      values.flexible ?? {},
      "Flexible Expenditure",
      categoryByKey,
      titleByKey,
    );
    const debtArr = recordToSectionArray(
      values.debts ?? {},
      "Additional Debt",
      categoryByKey,
      titleByKey,
    );

    try {
      await saveFinancialMutation.mutateAsync({
        createdWhen: dayjs().format("YYYY-MM-DD"),
        income: incomeArr,
        fixed: fixedArr,
        flexible: flexArr,
        debts: debtArr,
        monthlySavingsAmount: values.monthlySavingsAmount ?? 0,
        fixedOther: (values.fixedOther ?? []).map((fixed) => ({
          label: fixed.label,
          amount: fixed.amount,
          frequency: fixed.frequency,
        })),
        customersEmploymentStatus: values.aboutYou.customersEmploymentStatus,
        partnersEmploymentStatus: values.aboutYou.partnersEmploymentStatus,
        homeStatus: values.aboutYou.homeStatus,
        employmentType: values.aboutYou.employmentTypeId,
        employmentTitle: values.aboutYou.employmentTitleId,
        adults: values.aboutYou.adults,
        childrenUnder16: values.aboutYou.childrenUnder16,
        children16to18: values.aboutYou.children16to18,
        numberOfVehicles: values.aboutYou.numberOfVehicles,
        monthsSinceReview: 0,
        incomeAndExpenditureStatus: "Completed",
      });
      setTag(ANALYTICS_TAGS.SFS_COMPLETED);
    } catch (e: any) {
      throw new Error(e);
    }
  };

  if (financialQuery.isLoading && !financialQuery.data) {
    return <LinearProgress />;
  }

  return (
    <FormProvider {...methods}>
      <ScrollToTop />
      <Container sx={{ mt: 4, mb: 7 }} data-testid='sfs-stepper-container'>
        <Box display='flex' flexDirection='column' gap={4}>
          <Box>
            <Stepper
              nonLinear
              activeStep={activeStepIndex}
              alternativeLabel
              orientation='horizontal'
              data-testid='sfs-stepper'>
              {steps.map((s, idx) => (
                <Step key={s.id} completed={completed[idx] ?? false}>
                  <StepButton
                    color='inherit'
                    onClick={() => goToStep(idx)}
                    data-testid={`step-button-${s.id}`}>
                    {s.label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
          {activeStepId !== "about" && (
            <Box>
              <Typography
                variant='h5'
                component='h1'
                gutterBottom
                fontWeight='bold'
                data-testid='step-title'>
                {steps[activeStepIndex]?.label}
              </Typography>
            </Box>
          )}
          {(financialQuery.isError || saveFinancialMutation.isError) && (
            <Typography color='error' sx={{ mb: 2 }} data-testid='error-banner'>
              Oops:{" "}
              {financialQuery.error?.message ??
                saveFinancialMutation.error?.message}
            </Typography>
          )}
          <Box flex={1}>
            {activeStepId === "about" && (
              <Box data-testid='section-about'>
                <AboutYou financialData={financialQuery?.data} />
              </Box>
            )}

            {activeStepId === "income" && (
              <Box data-testid='section-income'>
                <CategoryInputsPanel
                  items={financialQuery.data?.income ?? buildIncome(null)}
                  namePrefix='income'
                  totals={totals}
                  parentTitle='Income'
                />
              </Box>
            )}

            {activeStepId === "fixed" && (
              <Box data-testid='section-fixed'>
                <CategoryInputsPanel
                  items={financialQuery.data?.fixed ?? buildFixed(null)}
                  namePrefix='fixed'
                  totals={totals}
                  parentTitle='Fixed Expenditure'
                  extraCategories={["Other"]}
                  renderForCategory={(cat) =>
                    cat === "Other" ? <FixedExtras /> : null
                  }
                />
              </Box>
            )}

            {activeStepId === "flexible" && (
              <Box data-testid='section-flexible'>
                <FlexibleStep
                  items={financialQuery.data?.flexible ?? buildFlexible(null)}
                  parentTitle='Flexible Expenditure'
                  namePrefix='flexible'
                  totals={totals}
                />
              </Box>
            )}

            {activeStepId === "debts" && (
              <Box data-testid='section-debts'>
                <CategoryInputsPanel
                  items={financialQuery.data?.debts ?? buildDebts(null)}
                  namePrefix='debts'
                  totals={totals}
                  parentTitle='Additional Debt'
                />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box display='flex' gap={2} justifyContent='space-between'>
              <Button
                variant='outlined'
                sx={{
                  marginRight: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                  marginBottom: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                  textTransform: "none",
                  borderRadius: 0,
                  fontSize: { xs: 17, sm: 17, md: 18, lg: 19, xl: 16 },
                }}
                onClick={prevStep}
                disabled={activeStepIndex === 0}
                data-testid='back-btn'>
                Back
              </Button>
              <Box display='flex' gap={2}>
                {activeStepIndex < steps.length - 1 ? (
                  <Button
                    variant='contained'
                    sx={{
                      marginRight: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                      marginBottom: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                      textTransform: "none",
                      borderRadius: 0,
                      fontSize: { xs: 17, sm: 17, md: 18, lg: 19, xl: 16 },
                    }}
                    onClick={() => {
                      nextStep();
                      setCompleted((c) => ({ ...c, [activeStepIndex]: true }));
                    }}
                    disabled={
                      activeStepIndex === 3 && !triggersAckCompleteFlexible
                    }
                    data-testid='next-btn'>
                    Next
                  </Button>
                ) : (
                  <Button
                    variant='contained'
                    sx={{
                      marginRight: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                      marginBottom: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 },
                      textTransform: "none",
                      borderRadius: 0,
                      fontSize: { xs: 17, sm: 17, md: 18, lg: 19, xl: 16 },
                    }}
                    disabled={saveFinancialMutation.isPending}
                    onClick={handleSubmit(onSubmit)}
                    data-testid='save-btn'>
                    {saveFinancialMutation.isPending
                      ? "Saving..."
                      : " Save & Continue"}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
        {setShowFooter(true)}
    </FormProvider>
  );
}

export default FinancialSteps;
